import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './EvisioChat.html?raw';

import _WidgetBaseSP from 'more/_WidgetBaseSP';
import FloatingContainer from '../../../UI/Floating/FloatingContainer';
import lang from 'dojo/_base/lang';
import { Message, MessageResponse, ThreadIdResponse } from "./EvisioChatTypes";
import GenericDialog from '../../../../moreCore/UI/GenericDialog/GenericDialog';

class EvisioChat extends compose(_WidgetBaseSP) {
    templateString: any = template;

    topContainer: HTMLDivElement;
    private bottomContainer: HTMLDivElement;

    private isOpen: boolean = false;
    private hasLoaded: boolean = false;

    private history: Array<Message>;
    private sessionId: string;

    private originalNode: HTMLElement;
    private chatContainer: HTMLDivElement;

    private messageInput: HTMLInputElement;

    private floatingContainer: MdsLax<InstanceType<typeof FloatingContainer>>;


    open(e: Event, button: HTMLElement) {
        if (!this.isOpen) {
            this.floatingContainer.open(e, this.domNode, true, null, button, null, true);
            if (!this.hasLoaded) {
                this.StandbyStart();
                this._setSessionIDs();
            }
            this.isOpen = true;
            sessionStorage.setItem("evisioIsOpen", "true");
        }
    }

    close(e: MouseEvent) {
        this.floatingContainer.close(e);
        this.isOpen = false;
        sessionStorage.removeItem("evisioIsOpen");
    }

    getIsOpen(): boolean {
        return this.isOpen;
    }

    private _onClickCloseButton(e: MouseEvent) {
        this.close(e);
        e.stopPropagation();
    }

    private createThreadId(): void {
        this.StandbyStart();
        this.RequestManager.callfunction("Webtool_API_EvisioAssistant_CreateThread", {},
            lang.hitch(this, this._onCreateThreadIdResponse),
            lang.hitch(this, this._onCreateThreadIdError)
        );
    }

    private _onCreateThreadIdResponse(response: ThreadIdResponse): void {
        this.StandbyStop();
        this.sessionId = response.threadId;
        localStorage.setItem("evisioChatId", this.sessionId);
        this._getHistory();
    }

    private _onCreateThreadIdError(error): void {
        this.StandbyStop();
        console.error(error);
    }

    private _getHistory(): void {
        if (!this.hasLoaded) {
            this.StandbyStart();
        }
        this.RequestManager.callfunction("Webtool_API_EvisioAssistant_GetThreadMessages", {
            threadId: this.sessionId
        },
            lang.hitch(this, this._onGetHistoryResponse),
            lang.hitch(this, this._onGetHistoryError),
        );
    }

    private _onGetHistoryResponse(response: MessageResponse): void {
        if (!this.hasLoaded) {
            this.StandbyStop();
        }
        this.hasLoaded = true;
        this.processHistory(response);
    }

    private _onGetHistoryError(error): void {
        this.StandbyStop();
        console.error(error);
    }

    private processHistory(history: MessageResponse): void {
        var messages = history.messages;
        this.history = [];

        for (var i = 0; i < messages.length; i++) {
            var messageObject = messages[i][0];

            var newMessage: Message = {
                text: messageObject.text.value,
                role: messages[i].role,
                created_at: new Date(messages[i].created_at)
            }
            this.history.push(newMessage);
            this.history.sort()
        }
        this.renderChat();
    }

    private renderChat(): void {
        this.chatContainer.innerHTML = null;
        var previousDate: Date = null;
        var currentDate: Date = null;
        for (var i = 0; i < this.history.length; i++) {
            var tempDate = this.history[i].created_at;
            if (!currentDate) {
                currentDate = tempDate;
            } else {
                if (
                    currentDate.getDate() > tempDate.getDate() ||
                    currentDate.getMonth() > tempDate.getMonth() ||
                    currentDate.getFullYear() > tempDate.getFullYear()
                ) {
                    previousDate = currentDate;
                    currentDate = tempDate;
                    this.createDateLabel(previousDate);
                }
            }

            this.createChatBubble(this.history[i]);

            if (previousDate) {
                if (
                    i === this.history.length - 1 &&
                    previousDate.getDate() > currentDate.getDate() ||
                    previousDate.getMonth() > currentDate.getMonth() ||
                    previousDate.getFullYear() > currentDate.getFullYear()) {

                    this.createDateLabel(currentDate);
                }
            } else if (i === this.history.length - 1) {
                this.createDateLabel(currentDate);
            }

        }
    }

    private createDateLabel(date: Date) {
        var dateLabelContainer = this.UiModder.domConstruct.create("div", null, this.chatContainer);
        this.UiModder.domClass.add(dateLabelContainer, "evisio-chat-date-label-container");

        var dateLabel = this.UiModder.domConstruct.create("span");
        this.UiModder.domClass.add(dateLabel, "evisio-chat-date-label");

        dateLabel.innerText = this._convertDateToLabel(date);

        this.UiModder.domConstruct.place(dateLabel, dateLabelContainer);
    }

    private _convertDateToLabel(date: Date) {
        var dateNow = new Date();

        var dayDifference = this._calculateDayDifference(dateNow, date);

        var label = "";

        if (dayDifference === 0) {
            label = this.translate("Evisio_Label_Today");
        } else if (dayDifference === 1) {
            label = this.translate("Evisio_Label_Yesterday");
        }
        else if (dayDifference > 1 && dayDifference < 7) {
            label = this.translate("Evisio_Label_Day" + date.getDay());
        }
        else {
            label = date.toLocaleDateString([], { weekday: "short", month: "short", year: "numeric", day: "numeric" });
        }

        return label;
    }

    private _calculateDayDifference(date1: Date, date2: Date) {
        const _MS_PER_DAY = 1000 * 60 * 60 * 24;
        // Discard the time and time-zone information.
        const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
        const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());

        return Math.abs(Math.floor((utc2 - utc1) / _MS_PER_DAY));
    }

    private createChatBubble(message: Message, index?: number): void {
        var chatBubble = this.UiModder.domConstruct.create("div", null, this.chatContainer, index);
        this.UiModder.domClass.add(chatBubble, "chat-bubble");

        var timestampContainer = this.UiModder.domConstruct.create("div");
        this.UiModder.domClass.add(timestampContainer, "chat-bubble-timestamp-container")

        var timestamp = this.UiModder.domConstruct.create("span", null, timestampContainer);
        this.UiModder.domClass.add(timestamp, "chat-bubble-timestamp");

        if (message.role === "user") this.UiModder.domClass.add(chatBubble, "receiver-chat-bubble");
        if (message.role === "assistant") this.UiModder.domClass.add(chatBubble, "sender-chat-bubble");

        chatBubble.innerText = message.text;

        timestamp.innerText = this._convertDateToTime(message.created_at);

        this.UiModder.domConstruct.place(timestampContainer, chatBubble);

    }

    private _convertDateToTime(date: Date) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: "2-digit" });
    }


    private _setSessionIDs(): void {
        var id = localStorage.getItem("evisioChatId");
        if (id) {
            this.sessionId = id;
            this._getHistory();
        } else {
            this.createThreadId();
        }
    }

    private setTyping(isTyping: boolean = true): void {
        if (isTyping) {
            this.createTypingBubble();
        }
        this.disableNode(this.messageInput, isTyping);
        if (!isTyping) this.messageInput.focus();
    }

    private createTypingBubble(): void {
        var typingBubble = this.UiModder.domConstruct.create("div", null, this.chatContainer, -1);
        this.UiModder.domClass.add(typingBubble, "chat-bubble sender-chat-bubble chat-bubble-typing");
        //Dots
        for (var i = 0; i < 3; i++) {
            var dot = this.UiModder.domConstruct.create("div", null, typingBubble);
            this.UiModder.domClass.add(dot, "typing-circle typing-scaling");

        }
    }

    private destroyTypingBubble() {
        let chatBbl = this.chatContainer.querySelectorAll(".chat-bubble-typing") as any as Array<HTMLDivElement>;
        for (let elem of chatBbl) {
            elem.remove();
        }
    }

    private _sendRequest(msg: string) {
        this.setTyping();
        this.RequestManager.callfunction("Webtool_API_EvisioAssistant_SendMessage", {
            threadId: this.sessionId,
            message: msg
        },
            lang.hitch(this, this._onSendRequestResponse),
            lang.hitch(this, this._onSendRequestError)
        );
    }

    private _onSendRequestResponse(response: MessageResponse) {
        this._getHistory();
        this.setTyping(false);
    }

    private _onSendRequestError(error) {
        console.error(error);
        this._getHistory();
        this.setTyping(false);
        this.destroyTypingBubble(); //da die chatbubble ansonsten beleibt...
        this.StandbyStop();
    }


    private _onClickSendButton(): void {
        var msg = this.messageInput.value;

        if (msg) {
            var newMessage: Message = {
                text: msg,
                role: "user",
                created_at: new Date()
            }
            this.createChatBubble(newMessage, 0);
            this.messageInput.value = "";
            this._sendRequest(msg);
        }
    }

    private _onClickClearButton(): void {
        var dlg = new GenericDialog();
        dlg.open(this.translate("Evisio_Warning_ClearHistory"), this.translate("Evisio_Heading_ClearHistory"), null, "fas fa-exclamation-triangle", "okcancel", lang.hitch(this, this._onClearDlgCallback));
    }

    private _onClearDlgCallback(response: number) {
        if (response === 1) {
            localStorage.removeItem("evisioChatId");
            this.history = [];
            this.hasLoaded = false;
            this.sessionId = "";
            this.createThreadId();
        }
    }

    private _onKeydownMessageInput(e: KeyboardEvent): void {
        if (e.key === "Enter") {
            this._onClickSendButton.bind(this)();
        }
    }

    private _onInputMessageInput(e: Event): void {
        var element = e.target as HTMLInputElement;
        element.style.height = "1em";
        element.style.height = (element.scrollHeight) + "px";
    }

    private _onScroll(e) {
        console.log(e);
    }

    setup(): void {
        this.floatingContainer = new FloatingContainer();
        this.floatingContainer.on("onscroll", lang.hitch(this, this._onScroll));
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.messageInput.addEventListener("keydown", lang.hitch(this, this._onKeydownMessageInput));
        this.messageInput.addEventListener("input", lang.hitch(this, this._onInputMessageInput));


        this.RequestManager.handleAs = "jsonWithDate";

        this.history = [];

        this.originalNode = this.domNode.cloneNode(true) as HTMLElement;
    }
    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(EvisioChat);
